import { useSearchParams } from 'react-router';
import { PAINT_SPEC_REPORT, REPORT_PARAM } from '@/app/paths';
import { useGenerationStats } from '@/features/history';
import { ArchitecturalAnalysisCard } from './components/ArchitecturalAnalysisCard';
import { ColorConfigurator } from './components/color/ColorConfigurator';
import { GeneratePanel } from './components/GeneratePanel';
import { HistoryShortcut } from './components/HistoryShortcut';
import { ImageUploader } from './components/ImageUploader';
import { LandscapingSelector } from './components/landscaping/LandscapingSelector';
import { ModelSelector } from './components/ModelSelector';
import { PaintSpecReportModal } from './components/PaintSpecReportModal';
import { PromptInspector } from './components/prompt/PromptInspector';
import { RenderPreview } from './components/RenderPreview';
import { useImageAspectRatio } from './hooks/useImageAspectRatio';
import { useStudio } from './state/studio-context';

export function StudioPage() {
  const studio = useStudio();
  const { state } = studio;
  const aspectRatio = useImageAspectRatio(state.image);
  const { data: stats } = useGenerationStats();

  const [searchParams, setSearchParams] = useSearchParams();
  const reportOpen = searchParams.get(REPORT_PARAM) === PAINT_SPEC_REPORT;
  const closeReport = () =>
    setSearchParams(
      (params) => {
        params.delete(REPORT_PARAM);
        return params;
      },
      { replace: true },
    );

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-5">
          <ImageUploader image={state.image} onImageSelect={studio.selectImage} />
          <ColorConfigurator colors={state.colors} onChange={studio.setColor} />
          <LandscapingSelector config={state.landscaping} onChange={studio.setLandscaping} />
          <PromptInspector
            masterPrompt={state.masterPrompt}
            onMasterPromptChange={studio.setMasterPrompt}
            colors={state.colors}
            landscaping={state.landscaping}
            model={state.model}
            aspectRatio={aspectRatio}
            image={state.image}
          />
          <ModelSelector value={state.model} onChange={studio.setModel} />
          <GeneratePanel
            onGenerate={studio.generate}
            isGenerating={studio.isGenerating}
            disabled={!state.image}
            status={studio.generationStatus}
            error={studio.generationError}
          />
        </div>

        <div className="lg:col-span-7 space-y-5">
          <RenderPreview originalImage={state.image} renderImage={state.result?.renderUrl ?? null} />
          {state.result?.analysis && <ArchitecturalAnalysisCard analysis={state.result.analysis} />}
          <HistoryShortcut count={stats?.count ?? 0} />
        </div>
      </div>

      <PaintSpecReportModal open={reportOpen} onClose={closeReport} colors={state.colors} />
    </>
  );
}
